import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { 
  Users, 
  Plus, 
  Search, 
  Filter, 
  ExternalLink, 
  Edit3, 
  Trash2, 
  X, 
  CheckCircle2, 
  BookOpen, 
  DollarSign, 
  CreditCard, 
  Layers,
  Sparkles
} from 'lucide-react';
import { CustomerRecord, CustomerType, CustomerCrmStage } from '@ftt/shared';

export const AdminCustomersPage: React.FC = () => {
  const [selectedType, setSelectedType] = useState<string>('ALL');
  const [selectedStage, setSelectedStage] = useState<string>('ALL');
  const [searchQuery, setSearchQuery] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState<CustomerRecord | null>(null);

  // Form state
  const [name, setName] = useState('');
  const [organization, setOrganization] = useState('');
  const [customerType, setCustomerType] = useState<CustomerType>('AUTHOR');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [country, setCountry] = useState('India');
  const [city, setCity] = useState('');
  const [totalContractValue, setTotalContractValue] = useState(0);
  const [totalPaidAmount, setTotalPaidAmount] = useState(0);
  const [crmStage, setCrmStage] = useState<CustomerCrmStage>('IN_PRODUCTION');
  const [assignedStaffName, setAssignedStaffName] = useState('Jonathan D.');
  const [notes, setNotes] = useState('');

  const queryClient = useQueryClient();

  const { data: customersRes, isLoading } = useQuery({
    queryKey: ['admin-customers', { type: selectedType, stage: selectedStage, search: searchQuery }],
    queryFn: () => {
      let url = '/admin/customers';
      const params = new URLSearchParams();
      if (selectedType !== 'ALL') params.append('type', selectedType);
      if (selectedStage !== 'ALL') params.append('stage', selectedStage);
      if (searchQuery) params.append('search', searchQuery);
      if (params.toString()) url += `?${params.toString()}`;
      return api.get(url).then((res) => res.data.data);
    },
  });

  const customers: CustomerRecord[] = customersRes || [];

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload = {
        name,
        organization: organization || null,
        customerType,
        email,
        phone: phone || null,
        country,
        city: city || null,
        totalContractValue,
        totalPaidAmount,
        crmStage,
        assignedStaffName: assignedStaffName || null,
        notes: notes || null,
      };

      if (editingCustomer?.id) {
        return api.put(`/admin/customers/${editingCustomer.id}`, payload);
      } else {
        return api.post('/admin/customers', payload);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-customers'] });
      setIsModalOpen(false);
      resetForm();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/admin/customers/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-customers'] });
    },
  });

  const resetForm = () => {
    setEditingCustomer(null);
    setName('');
    setOrganization('');
    setCustomerType('AUTHOR');
    setEmail('');
    setPhone('');
    setCountry('India');
    setCity('');
    setTotalContractValue(0);
    setTotalPaidAmount(0);
    setCrmStage('IN_PRODUCTION');
    setAssignedStaffName('Jonathan D.');
    setNotes('');
  };

  const handleOpenEdit = (customer: CustomerRecord) => {
    setEditingCustomer(customer);
    setName(customer.name);
    setOrganization(customer.organization || '');
    setCustomerType(customer.customerType);
    setEmail(customer.email);
    setPhone(customer.phone || '');
    setCountry(customer.country);
    setCity(customer.city || '');
    setTotalContractValue(customer.totalContractValue);
    setTotalPaidAmount(customer.totalPaidAmount);
    setCrmStage(customer.crmStage);
    setAssignedStaffName(customer.assignedStaffName || 'Jonathan D.');
    setNotes(customer.notes || '');
    setIsModalOpen(true);
  };

  // Metrics
  const totalContractRevenue = customers.reduce((sum, c) => sum + c.totalContractValue, 0);
  const totalPaidRevenue = customers.reduce((sum, c) => sum + c.totalPaidAmount, 0);
  const totalOutstanding = customers.reduce((sum, c) => sum + c.outstandingBalance, 0);

  return (
    <div className="space-y-6 font-sans text-slate-900 antialiased">
      
      {/* 1. Header & Primary Action */}
      <div className="bg-white rounded-2xl md:rounded-3xl p-6 shadow-sm border border-slate-200/90 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <span className="text-[10px] text-brand-700 font-mono uppercase tracking-widest font-bold">
              CRM &amp; AUTHOR PIPELINE
            </span>
            <span className="text-slate-400 font-mono text-xs">&bull;</span>
            <span className="text-xs text-slate-600 font-serif italic">
              FOR THE TRUTH PRESS PRIVATE LIMITED
            </span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-serif font-black text-slate-900 flex items-center gap-2.5 mt-1">
            <Users className="w-7 h-7 text-[#FF6B4A]" />
            <span>Customer &amp; Author Directory</span>
          </h1>
          <p className="text-xs sm:text-sm text-slate-600 font-medium mt-1">
            Track client relationships, publishing contracts, lifetime revenue, and outstanding milestone balances.
          </p>
        </div>

        <button
          onClick={() => {
            resetForm();
            setIsModalOpen(true);
          }}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-extrabold text-xs shadow-md transition-all shrink-0 cursor-pointer"
        >
          <Plus className="w-4 h-4 text-[#FF6B4A]" />
          <span>Add New Customer</span>
        </button>
      </div>

      {/* 2. Metric Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="p-5 rounded-2xl bg-white border border-slate-200/90 shadow-sm space-y-1">
          <span className="text-[10px] font-mono uppercase tracking-wider text-slate-600 font-bold">Total Accounts</span>
          <div className="text-2xl font-black text-slate-900 font-serif">{customers.length}</div>
          <span className="text-[11px] text-slate-600 font-bold">Contracted authors &amp; ministries</span>
        </div>

        <div className="p-5 rounded-2xl bg-white border border-slate-200/90 shadow-sm space-y-1">
          <span className="text-[10px] font-mono uppercase tracking-wider text-brand-700 font-bold">Contracted Value</span>
          <div className="text-2xl font-black text-brand-700 font-serif">₹{totalContractRevenue.toLocaleString('en-IN')}</div>
          <span className="text-[11px] text-slate-600 font-bold">Cumulative publishing fees</span>
        </div>

        <div className="p-5 rounded-2xl bg-white border border-slate-200/90 shadow-sm space-y-1">
          <span className="text-[10px] font-mono uppercase tracking-wider text-emerald-700 font-bold">Collected Cash</span>
          <div className="text-2xl font-black text-emerald-700 font-serif">₹{totalPaidRevenue.toLocaleString('en-IN')}</div>
          <span className="text-[11px] text-slate-600 font-bold">Paid to date</span>
        </div>

        <div className="p-5 rounded-2xl bg-white border border-slate-200/90 shadow-sm space-y-1">
          <span className="text-[10px] font-mono uppercase tracking-wider text-amber-700 font-bold">Outstanding Receivables</span>
          <div className="text-2xl font-black text-amber-700 font-serif">₹{totalOutstanding.toLocaleString('en-IN')}</div>
          <span className="text-[11px] text-slate-600 font-bold">Pending milestone invoices</span>
        </div>
      </div>

      {/* 3. Filter and Search Bar */}
      <div className="p-4 rounded-2xl bg-white border border-slate-200/90 shadow-sm flex flex-wrap items-center justify-between gap-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative">
            <Search className="w-3.5 h-3.5 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              placeholder="Search author, email or org..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 pr-4 py-2 rounded-xl bg-slate-50 border border-slate-200 text-xs text-slate-900 font-semibold placeholder-slate-400 focus:outline-hidden focus:ring-2 focus:ring-brand-500 w-56 sm:w-64"
            />
          </div>

          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-600 font-bold">Type:</span>
            <select
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value)}
              className="px-3 py-2 rounded-xl bg-slate-50 border border-slate-200 text-xs text-slate-800 font-bold focus:outline-hidden cursor-pointer"
            >
              <option value="ALL">All Types</option>
              <option value="AUTHOR">Author</option>
              <option value="MINISTRY_LEADER">Ministry Leader</option>
              <option value="SCHOLAR">Scholar</option>
              <option value="INSTITUTION">Institution</option>
            </select>
          </div>
        </div>

        <div className="text-xs text-slate-600 font-mono font-bold">
          Showing <strong className="text-slate-900">{customers.length}</strong> clients
        </div>
      </div>

      {/* 4. Table */}
      <div className="bg-white rounded-2xl md:rounded-3xl border border-slate-200/90 overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs text-slate-800">
            <thead className="bg-slate-50 text-slate-700 font-mono text-[11px] uppercase border-b border-slate-200 font-bold">
              <tr>
                <th className="py-4 px-4">Author / Client</th>
                <th className="py-4 px-4">Type &amp; Affiliation</th>
                <th className="py-4 px-4">CRM Status</th>
                <th className="py-4 px-4">Contract Value</th>
                <th className="py-4 px-4">Paid / Outstanding</th>
                <th className="py-4 px-4">Assigned Lead</th>
                <th className="py-4 px-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 font-sans font-medium">
              {isLoading ? (
                <tr>
                  <td colSpan={7} className="py-12 text-center text-xs font-mono text-slate-500">
                    Loading customer records...
                  </td>
                </tr>
              ) : customers.length === 0 ? (
                <tr>
                  <td colSpan={7} className="py-12 text-center text-xs text-slate-500">
                    No customers found matching filters.
                  </td>
                </tr>
              ) : (
                customers.map((c) => (
                  <tr key={c.id} className="hover:bg-slate-50/80 transition-colors">
                    <td className="py-4 px-4">
                      <div>
                        <span className="font-serif font-black text-sm text-slate-900 block">{c.name}</span>
                        <span className="text-[11px] text-slate-500 font-mono">{c.email}</span>
                      </div>
                    </td>

                    <td className="py-4 px-4">
                      <span className="text-[10px] font-mono px-2 py-0.5 rounded-md bg-brand-50 border border-brand-200 text-brand-800 font-bold block w-fit mb-1">
                        {c.customerType}
                      </span>
                      <span className="text-xs text-slate-600 font-medium">{c.organization || 'Independent'} &bull; {c.country}</span>
                    </td>

                    <td className="py-4 px-4">
                      <span className={`text-[10px] font-mono px-2.5 py-1 rounded-md font-bold ${
                        c.crmStage === 'IN_PRODUCTION'
                          ? 'bg-blue-100 text-blue-900 border border-blue-200'
                          : c.crmStage === 'REPEAT_CLIENT'
                          ? 'bg-emerald-100 text-emerald-900 border border-emerald-200'
                          : 'bg-slate-100 text-slate-800 border border-slate-200'
                      }`}>
                        {c.crmStage.replace(/_/g, ' ')}
                      </span>
                    </td>

                    <td className="py-4 px-4 font-mono font-bold text-slate-900">
                      ₹{c.totalContractValue.toLocaleString('en-IN')}
                    </td>

                    <td className="py-4 px-4 font-mono">
                      <div className="text-emerald-700 font-bold">₹{c.totalPaidAmount.toLocaleString('en-IN')}</div>
                      {c.outstandingBalance > 0 ? (
                        <div className="text-amber-700 font-bold text-[10px]">₹{c.outstandingBalance.toLocaleString('en-IN')} pending</div>
                      ) : (
                        <div className="text-slate-500 text-[10px]">Settled</div>
                      )}
                    </td>

                    <td className="py-4 px-4 text-xs font-mono text-slate-600 font-bold">
                      {c.assignedStaffName || 'Unassigned'}
                    </td>

                    <td className="py-4 px-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleOpenEdit(c)}
                          className="p-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 hover:text-slate-900 transition-colors cursor-pointer"
                          title="Edit Customer"
                        >
                          <Edit3 className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => {
                            if (confirm(`Remove customer "${c.name}"?`)) {
                              deleteMutation.mutate(c.id);
                            }
                          }}
                          className="p-1.5 rounded-lg bg-rose-50 hover:bg-rose-100 text-rose-600 hover:text-rose-700 transition-colors cursor-pointer"
                          title="Delete Customer"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add / Edit Customer Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs overflow-y-auto">
          <div className="bg-white border border-slate-200 rounded-3xl max-w-2xl w-full p-6 sm:p-8 space-y-6 shadow-2xl max-h-[90vh] overflow-y-auto text-slate-900">
            <div className="flex items-center justify-between border-b border-slate-100 pb-4">
              <h2 className="text-xl font-serif font-bold text-slate-900">
                {editingCustomer ? 'Edit Customer / Author' : 'Add New Customer / Author'}
              </h2>
              <button
                onClick={() => setIsModalOpen(false)}
                className="p-1.5 rounded-lg text-slate-400 hover:text-slate-900 hover:bg-slate-100 cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                saveMutation.mutate();
              }}
              className="space-y-4 text-xs font-sans"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label className="text-slate-700 font-bold block">Author / Customer Name *</label>
                  <input
                    type="text"
                    required
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="e.g. Dr. Arthur Pendleton"
                    className="w-full px-3.5 py-2 rounded-xl bg-slate-50 border border-slate-200 text-slate-900 font-semibold focus:outline-hidden"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-slate-700 font-bold block">Customer Type *</label>
                  <select
                    value={customerType}
                    onChange={(e) => setCustomerType(e.target.value as CustomerType)}
                    className="w-full px-3.5 py-2 rounded-xl bg-slate-50 border border-slate-200 text-slate-900 font-semibold focus:outline-hidden"
                  >
                    <option value="AUTHOR">Author / Writer</option>
                    <option value="MINISTRY_LEADER">Ministry Leader / Pastor</option>
                    <option value="SCHOLAR">Theological Scholar / Professor</option>
                    <option value="INSTITUTION">Seminary / Institutional Board</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label className="text-slate-700 font-bold block">Email Address *</label>
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="author@seminary.org"
                    className="w-full px-3.5 py-2 rounded-xl bg-slate-50 border border-slate-200 text-slate-900 font-semibold focus:outline-hidden"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-slate-700 font-bold block">Organization / Affiliation</label>
                  <input
                    type="text"
                    value={organization}
                    onChange={(e) => setOrganization(e.target.value)}
                    placeholder="e.g. Grace Seminary"
                    className="w-full px-3.5 py-2 rounded-xl bg-slate-50 border border-slate-200 text-slate-900 font-semibold focus:outline-hidden"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="space-y-1">
                  <label className="text-slate-700 font-bold block">Phone Number</label>
                  <input
                    type="text"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="+91 98450 12345"
                    className="w-full px-3.5 py-2 rounded-xl bg-slate-50 border border-slate-200 text-slate-900 font-semibold focus:outline-hidden"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-slate-700 font-bold block">City</label>
                  <input
                    type="text"
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    placeholder="Hyderabad"
                    className="w-full px-3.5 py-2 rounded-xl bg-slate-50 border border-slate-200 text-slate-900 font-semibold focus:outline-hidden"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-slate-700 font-bold block">Country *</label>
                  <input
                    type="text"
                    required
                    value={country}
                    onChange={(e) => setCountry(e.target.value)}
                    placeholder="India"
                    className="w-full px-3.5 py-2 rounded-xl bg-slate-50 border border-slate-200 text-slate-900 font-semibold focus:outline-hidden"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="space-y-1">
                  <label className="text-slate-700 font-bold block">Total Contract (INR)</label>
                  <input
                    type="number"
                    value={totalContractValue}
                    onChange={(e) => setTotalContractValue(Number(e.target.value))}
                    className="w-full px-3.5 py-2 rounded-xl bg-slate-50 border border-slate-200 text-slate-900 font-semibold focus:outline-hidden"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-slate-700 font-bold block">Paid to Date (INR)</label>
                  <input
                    type="number"
                    value={totalPaidAmount}
                    onChange={(e) => setTotalPaidAmount(Number(e.target.value))}
                    className="w-full px-3.5 py-2 rounded-xl bg-slate-50 border border-slate-200 text-slate-900 font-semibold focus:outline-hidden"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-slate-700 font-bold block">CRM Stage</label>
                  <select
                    value={crmStage}
                    onChange={(e) => setCrmStage(e.target.value as CustomerCrmStage)}
                    className="w-full px-3.5 py-2 rounded-xl bg-slate-50 border border-slate-200 text-slate-900 font-semibold focus:outline-hidden"
                  >
                    <option value="PROSPECT">Prospect</option>
                    <option value="CONTRACTED">Contracted</option>
                    <option value="IN_PRODUCTION">In Production</option>
                    <option value="REPEAT_CLIENT">Repeat Client</option>
                  </select>
                </div>
              </div>

              <div className="space-y-1">
                <label className="text-slate-700 font-bold block">Assigned Lead / Staff</label>
                <input
                  type="text"
                  value={assignedStaffName}
                  onChange={(e) => setAssignedStaffName(e.target.value)}
                  className="w-full px-3.5 py-2 rounded-xl bg-slate-50 border border-slate-200 text-slate-900 font-semibold focus:outline-hidden"
                />
              </div>

              <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="px-4 py-2 rounded-xl border border-slate-200 text-slate-700 hover:bg-slate-100 font-bold cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saveMutation.isPending}
                  className="px-5 py-2 rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-bold disabled:opacity-50 cursor-pointer shadow-sm"
                >
                  {saveMutation.isPending ? 'Saving...' : editingCustomer ? 'Update Customer' : 'Save Customer Record'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
};
